/**
 * Shared canvas geometry helpers for the drawing surface(s).
 *
 * Both Canvas.tsx (legacy SVG) and KonvaCanvas (Konva chunk 2+)
 * lasso-rect hit-tests against per-primitive bounding boxes and
 * seed curved-arrow control points with the same default. The
 * helpers live here so the two surfaces stay byte-identical on
 * the math front — porting Konva's tool flows must not introduce
 * subtle hit-test or curve-shape drifts.
 */
import type { CanvasPrimitive } from '@/api/client';

export interface BBox {
  minX: number; maxX: number; minY: number; maxY: number;
}

/** Rough AABB per primitive type. Used by the lasso-rect drag's
 *  overlap test on release. Numerals get a 24px box (chip radius
 *  20 + a little padding); text uses ``len * 8`` as a width
 *  estimate (matches Canvas.tsx); lasso primitives (legacy, never
 *  emitted by current FE but still in the type union for export-
 *  contract reasons) span their own polygon. */
export function primitiveBoundingBox(
  p: CanvasPrimitive,
): BBox | null {
  const d = p.primitive_data_json as Record<string, unknown>;
  switch (p.primitive_type) {
    case 'numeral': {
      const x = (d.x as number) ?? 0;
      const y = (d.y as number) ?? 0;
      return { minX: x - 24, maxX: x + 24, minY: y - 24, maxY: y + 24 };
    }
    case 'arrow':
    case 'curved_arrow': {
      const x1 = (d.x1 as number) ?? 0;
      const y1 = (d.y1 as number) ?? 0;
      const x2 = (d.x2 as number) ?? 0;
      const y2 = (d.y2 as number) ?? 0;
      return {
        minX: Math.min(x1, x2), maxX: Math.max(x1, x2),
        minY: Math.min(y1, y2), maxY: Math.max(y1, y2),
      };
    }
    case 'text': {
      const x = (d.x as number) ?? 0;
      const y = (d.y as number) ?? 0;
      const text = (d.text as string) || '';
      const w = Math.max(20, text.length * 8);
      return { minX: x, maxX: x + w, minY: y - 16, maxY: y + 4 };
    }
    case 'lasso': {
      const pts = (d.points as Array<{ x: number; y: number }>) || [];
      if (pts.length === 0) return null;
      const xs = pts.map((pt) => pt.x);
      const ys = pts.map((pt) => pt.y);
      return {
        minX: Math.min(...xs), maxX: Math.max(...xs),
        minY: Math.min(...ys), maxY: Math.max(...ys),
      };
    }
    default:
      return null;
  }
}

/** Compute a curve control point ~30% perpendicular to the
 * straight line from (x1,y1) to (x2,y2). Used both at draw time
 * to seed the bezier and at render time when an older row lacks
 * explicit cx/cy. */
export function defaultBezierControl(
  x1: number, y1: number, x2: number, y2: number,
): { cx: number; cy: number } {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  // Unit perpendicular (rotate +90°), scale by 30% of the line.
  const px = -dy / len;
  const py =  dx / len;
  const offset = len * 0.30;
  return { cx: mx + px * offset, cy: my + py * offset };
}

/** Quadratic-bezier sample → polyline. Konva.Line / Konva.Arrow
 *  with ``tension=0`` matches an SVG <path d="M…Q…">; sampling at
 *  16 segments is visually indistinguishable from the SVG version
 *  at any reasonable zoom level. */
export function quadBezierPoints(
  x1: number, y1: number,
  cx: number, cy: number,
  x2: number, y2: number,
  segments = 16,
): number[] {
  const out: number[] = [];
  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    const mt = 1 - t;
    const x = mt * mt * x1 + 2 * mt * t * cx + t * t * x2;
    const y = mt * mt * y1 + 2 * mt * t * cy + t * t * y2;
    out.push(x, y);
  }
  return out;
}
